import { useState, useEffect, useCallback } from 'react';
import { listInvites, createInvite, revokeInvite, resendInvite, type Invite } from '@/lib/api/invites';
import { getEmployees, type Employee } from '@/lib/api/employees';
import { getClients, type Client } from '@/lib/api/clients';

const PORTAL_BASE = window.location.origin + '/portal/register';

export const InvitesTab = () => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    invite_type: 'employee' as 'employee' | 'client',
    target_id: '' as string, // '' = not linked to an existing record
    email: '',
    expires_days: 7,
    is_reset: false,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setInvites(await listInvites());
    } catch {
      setError('Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getEmployees().then(setEmployees).catch(() => {});
    getClients().then(r => setClients(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);

  // Selecting an existing person pre-fills their email and suggests reset mode
  // when they already have a portal account.
  const pickTarget = (id: string) => {
    if (!id) { setForm(f => ({ ...f, target_id: '', is_reset: false })); return; }
    if (form.invite_type === 'employee') {
      const emp = employees.find(e => String(e.id) === id);
      setForm(f => ({ ...f, target_id: id, email: emp?.email || f.email, is_reset: !!emp?.user_id }));
    } else {
      const c = clients.find(x => String(x.id) === id);
      setForm(f => ({ ...f, target_id: id, email: c?.email || f.email, is_reset: true }));
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setBusy(true);
    setError('');
    try {
      await createInvite({
        invite_type: form.invite_type,
        email: form.email.trim(),
        expires_days: form.expires_days,
        is_reset: form.is_reset,
        target_employee_id: form.invite_type === 'employee' && form.target_id ? Number(form.target_id) : undefined,
        target_client_id: form.invite_type === 'client' && form.target_id ? Number(form.target_id) : undefined,
      });
      setForm({ invite_type: 'employee', target_id: '', email: '', expires_days: 7, is_reset: false });
      setShowForm(false);
      setNotice('Invite created and emailed.');
      setTimeout(() => setNotice(''), 4000);
      await load();
    } catch {
      setError('Failed to create invite');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!confirm('Revoke this invite? The link will stop working immediately.')) return;
    setBusyId(id);
    try {
      await revokeInvite(id);
      await load();
    } catch {
      setError('Failed to revoke invite');
    } finally {
      setBusyId(null);
    }
  };

  const handleResend = async (inv: Invite) => {
    setBusyId(inv.id);
    setError('');
    try {
      await resendInvite(inv.id);
      setNotice(`Invite re-sent to ${inv.email}${isExpired(inv) ? ' (expiry renewed +7 days)' : ''}.`);
      setTimeout(() => setNotice(''), 4000);
      await load();
    } catch {
      setError('Failed to resend invite');
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = (invite: Invite) => {
    const url = invite.inviteUrl || `${PORTAL_BASE}?code=${invite.code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(invite.id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const isExpired = (inv: Invite) => new Date(inv.expires_at).getTime() < Date.now();
  const targetName = (inv: Invite) => inv.target_employee_name || inv.target_client_name || null;

  const pending = invites.filter(i => !i.used_at);
  const used = invites.filter(i => i.used_at);

  const targetOptions = form.invite_type === 'employee'
    ? employees.map(e => ({ id: String(e.id), label: `${e.name}${e.user_id ? ' (has portal account)' : ''}` }))
    : clients.map(c => ({ id: String(c.id), label: c.name }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Portal Invites</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Single-use links for employees and clients to register — or regain access to — their portal accounts.
          </p>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 bg-[#C77D3F] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#b36e35]"
        >
          <span className="material-symbols-outlined text-base">add</span>
          New Invite
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{notice}</div>}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-gray-900">Generate Invite Link</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Portal Type</label>
              <select
                value={form.invite_type}
                onChange={e => setForm(f => ({ ...f, invite_type: e.target.value as 'employee' | 'client', target_id: '', is_reset: false }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
              >
                <option value="employee">Employee</option>
                <option value="client">Client</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link to existing {form.invite_type}</label>
              <select
                value={form.target_id}
                onChange={e => pickTarget(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
              >
                <option value="">— none (invite by email only) —</option>
                {targetOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires In</label>
              <select
                value={form.expires_days}
                onChange={e => setForm(f => ({ ...f, expires_days: Number(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
              >
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="worker@example.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
            />
          </div>
          <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_reset}
              onChange={e => setForm(f => ({ ...f, is_reset: e.target.checked }))}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium text-gray-800">Access reset (forgot password / lost access)</span>
              <span className="block text-xs text-gray-500">Lets someone who already has a portal account use this link to set a brand-new password. Their old logins are signed out.</span>
            </span>
          </label>
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={busy}
              className="bg-[#C77D3F] text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-[#b36e35] disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create & Send Invite'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-sm text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Pending invites */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h4 className="font-semibold text-gray-900">Pending <span className="text-gray-400 font-normal">({pending.length})</span></h4>
        </div>
        {loading ? (
          <div className="p-5 text-sm text-gray-400">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="p-5 text-sm text-gray-400">No pending invites.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {pending.map(inv => (
              <div key={inv.id} className="px-5 py-3.5 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-gray-900">{inv.email}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inv.invite_type === 'employee' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                      {inv.invite_type}
                    </span>
                    {targetName(inv) && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">
                        → {targetName(inv)}
                      </span>
                    )}
                    {!!inv.is_reset && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">access reset</span>
                    )}
                    {isExpired(inv) && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">expired</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {isExpired(inv) ? 'Expired' : 'Expires'} {new Date(inv.expires_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {inv.created_by_name && ` · Created by ${inv.created_by_name}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleResend(inv)}
                    disabled={busyId === inv.id}
                    title={isExpired(inv) ? 'Re-send and renew expiry (+7 days)' : 'Re-send invite email'}
                    className="flex items-center gap-1 text-xs text-gray-700 border border-gray-300 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <span className={`material-symbols-outlined text-base ${busyId === inv.id ? 'animate-spin' : ''}`}>
                      {busyId === inv.id ? 'progress_activity' : 'send'}
                    </span>
                    {isExpired(inv) ? 'Renew & Resend' : 'Resend'}
                  </button>
                  <button
                    onClick={() => copyLink(inv)}
                    title="Copy invite link"
                    className="flex items-center gap-1 text-xs text-[#C77D3F] hover:text-[#b36e35] border border-[#C77D3F]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#FAF6F0]"
                  >
                    <span className="material-symbols-outlined text-base">
                      {copied === inv.id ? 'check' : 'content_copy'}
                    </span>
                    {copied === inv.id ? 'Copied!' : 'Copy Link'}
                  </button>
                  <button
                    onClick={() => handleRevoke(inv.id)}
                    disabled={busyId === inv.id}
                    title="Revoke invite"
                    className="text-gray-400 hover:text-red-600 disabled:opacity-40"
                  >
                    <span className="material-symbols-outlined text-xl">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Used invites */}
      {used.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100">
            <h4 className="font-semibold text-sm text-gray-500">Used <span className="font-normal">({used.length})</span></h4>
          </div>
          <div className="divide-y divide-gray-100">
            {used.map(inv => (
              <div key={inv.id} className="px-5 py-3 flex items-center gap-3 opacity-60">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-gray-900">{inv.email}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inv.invite_type === 'employee' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                      {inv.invite_type}
                    </span>
                    {targetName(inv) && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600">→ {targetName(inv)}</span>
                    )}
                    {!!inv.is_reset && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">access reset</span>}
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">used</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Used {new Date(inv.used_at!).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
